"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Pencil, Trash2, Clock } from "lucide-react";
import { mockCategories } from "@/lib/mock-data";

export default function CategoriesPage() {
    const [categories] = useState(mockCategories);

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="heading-sm text-primary">จัดการประเภทเรื่อง</h1>
                    <p className="text-muted-foreground">กำหนดประเภทเรื่องร้องเรียนและระยะเวลา SLA</p>
                </div>
                <Dialog>
                    <DialogTrigger asChild>
                        <Button>
                            <Plus className="mr-2 h-4 w-4" />
                            เพิ่มประเภท
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>เพิ่มประเภทใหม่</DialogTitle>
                            <DialogDescription>กรอกข้อมูลประเภทเรื่องร้องเรียนที่ต้องการเพิ่ม</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>ชื่อประเภท</Label>
                                <Input placeholder="เช่น ร้องเรียนพฤติกรรมเจ้าหน้าที่" />
                            </div>
                            <div className="space-y-2">
                                <Label>รายละเอียด</Label>
                                <Input placeholder="คำอธิบายประเภทเรื่องร้องเรียน" />
                            </div>
                            <div className="space-y-2">
                                <Label>ระยะเวลาตอบกลับ (ชั่วโมง)</Label>
                                <Input type="number" placeholder="48" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button>บันทึก</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Categories Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => (
                    <Card key={category.id} className="relative">
                        <CardHeader className="pb-3">
                            <div className="flex items-start justify-between">
                                <CardTitle className="text-base pr-8">{category.name}</CardTitle>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="absolute top-4 right-4">
                                            <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem>
                                            <Pencil className="mr-2 h-4 w-4" />
                                            แก้ไข
                                        </DropdownMenuItem>
                                        <DropdownMenuItem className="text-destructive">
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            ลบ
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            <CardDescription className="line-clamp-2">{category.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-2 text-sm">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <span className="text-muted-foreground">SLA:</span>
                                <Badge variant="secondary">{category.slaHours} ชั่วโมง</Badge>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Info Card */}
            <Card className="bg-muted/50">
                <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Clock className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <h3 className="font-medium mb-1">เกี่ยวกับ SLA</h3>
                            <p className="text-sm text-muted-foreground">
                                SLA (Service Level Agreement) คือระยะเวลาที่กำหนดสำหรับการตอบกลับเรื่องร้องเรียนแต่ละประเภท
                                ระบบจะแจ้งเตือนเมื่อใกล้ครบกำหนดหรือเกินกำหนด
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
